import { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import BoxCard from "shared/components/box/BoxCard.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import { getMatches } from "shared/services/api-assistant.js";

export const getOpponentPosition = (competition, opponentId, view) => {
  const snapshots = competition.rankingHistory || [];
  const latest = snapshots[snapshots.length - 1];
  const ordered = view === "ranking"
    ? (latest?.rankings || []).map((club) => club.clubId)
    : [...(competition.participants || [])]
        .map((club) => {
          if (competition.status === "LIVE" || competition.status === "PLANNED") return club;
          const history = [...(club.history || [])].reverse()
            .find((entry) => latest?.date && entry.date <= latest.date && entry.mmr != null);
          return { id: club.id, mmr: history?.mmr ?? 0 };
        })
        .sort((a, b) => (b.mmr ?? 0) - (a.mmr ?? 0))
        .map((club) => club.id);
  const index = ordered.indexOf(opponentId);
  return index < 0 ? null : index + 1;
};

export const getMatchOutcome = (match, clubId) => {
  if (match.status !== "ENDED" || match.homeScore == null || match.awayScore == null) return "planned";
  const difference = match.homeClub.id === clubId
    ? match.homeScore - match.awayScore
    : match.awayScore - match.homeScore;
  return difference > 0 ? "win" : difference < 0 ? "loss" : "draw";
};

const MatchPlannerSchedule = ({ competition, club, opponent, clubMatches = [], selectedMatchId }) => {
  const [view, setView] = useState("ranking");
  const [opponentMatches, setOpponentMatches] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setOpponentMatches(null);
    setError(false);
    const collected = [];
    const fetchPage = (skip = 0) => getMatches({
      params: {
        clubs: [opponent.id],
        types: [competition.type],
        ...(competition.startingDate
          ? { minDate: competition.startingDate.slice(0, 10) }
          : { currentSeason: true }),
        limit: 100,
        skip,
      },
      handleSuccess: (response) => {
        if (!active) return;
        const page = response.data.getMatches;
        collected.push(...page);
        if (page.length === 100) {
          fetchPage(skip + 100);
          return;
        }
        setOpponentMatches([...new Map(collected.map((match) => [match.id, match])).values()]
          .filter((match) => String(match.competitionId) === String(competition.id))
          .sort((a, b) => a.startDate.localeCompare(b.startDate)));
      },
      handleError: () => {
        if (active) setError(true);
      },
    });
    fetchPage();
    return () => { active = false; };
  }, [competition.id, competition.type, competition.startingDate, club.id, opponent.id, retry]);

  return (
    <BoxCard
      className="match-planner-schedule m-0"
      title="Fixtures"
      contentClassName="flex-column"
      actions={
        <ButtonSelect
          ariaLabel="Opponent position"
          buttons={{ ranking: "Ranking", projection: "Projection" }}
          selected={view}
          onChange={setView}
        />
      }
      content={
        <section aria-label="Competition fixtures">
        <div className="match-planner-schedule-rows">
          {[club, opponent].map((team) => {
            const isOpponent = team.id === opponent.id;
            const teamMatches = isOpponent ? opponentMatches : clubMatches;
            const fixtures = (teamMatches || []).filter((match) =>
              String(match.competitionId) === String(competition.id) &&
              (match.homeClub.id === team.id || match.awayClub.id === team.id))
              .sort((a, b) => a.startDate.localeCompare(b.startDate));
            return (
              <div key={team.id} className="match-planner-schedule-row">
                <div className="small text-truncate mb-1" title={team.name}>{team.name}</div>
                {isOpponent && error ? (
                  <div role="alert">
                    Could not load fixtures.
                    <button className="btn btn-link btn-small" onClick={() => setRetry((value) => value + 1)}>Retry</button>
                  </div>
                ) : teamMatches === null ? (
                  <div aria-busy="true" aria-label={`Loading ${team.name} fixtures`}>
                    <LoadingSquare height="36px" />
                  </div>
                ) : (
                <div className="match-planner-schedule-squares" role="list" aria-label={`${team.name} fixtures`}>
                  {fixtures.length === 0 && <span className="small text-muted">No fixtures found</span>}
                  {fixtures.map((match) => {
                    const other = match.homeClub.id === team.id ? match.awayClub : match.homeClub;
                    const position = getOpponentPosition(competition, other.id, view);
                    const outcome = getMatchOutcome(match, team.id);
                    const result = match.status === "ENDED"
                      ? `${outcome}, ${match.homeScore ?? "?"}–${match.awayScore ?? "?"} (home–away)`
                      : match.status.toLowerCase();
                    const date = new Date(match.startDate).toLocaleString(undefined, {
                      dateStyle: "medium", timeStyle: "short",
                    });
                    const label = `${match.homeClub.name} vs ${match.awayClub.name}, ${date}, ` +
                      `${view} ${position ?? "unavailable"}, ${result}`;
                    return (
                      <span
                        key={match.id}
                        role="listitem"
                        className="match-planner-schedule-stop"
                      >
                        <Popup
                          on={["hover", "focus", "click"]}
                          position={["top center", "bottom center"]}
                          keepTooltipInside
                          mouseEnterDelay={100}
                          closeOnDocumentClick
                          className="match-planner-fixture-tooltip"
                          trigger={
                            <button
                              type="button"
                              aria-label={label}
                              aria-current={match.id === selectedMatchId ? "true" : undefined}
                              className={`match-planner-schedule-square match-planner-schedule-square--${outcome}`}
                            >
                              {position ?? "–"}
                            </button>
                          }
                        >
                          <div className="small text-center" role="tooltip">
                            <div className="fw-bold mb-1">{match.homeClub.name} vs {match.awayClub.name}</div>
                            <div className="fs-5 fw-bold">
                              {match.status === "PLANNED"
                                ? "Planned"
                                : `${match.homeScore ?? "–"} – ${match.awayScore ?? "–"}`}
                            </div>
                            <time dateTime={match.startDate}>{date}</time>
                          </div>
                        </Popup>
                      </span>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </div>
        </section>
      }
    />
  );
};

export default MatchPlannerSchedule;
